import { useEffect, useRef } from 'react';

/**
 * Registers a window-level Escape key listener that calls `onClose` while
 * the modal is open. Any dirty-state confirmation logic belongs inside
 * `onClose` itself (e.g. `tryClose` wrappers in form modals).
 *
 * `onClose` is stored in a ref so the listener always calls the latest version
 * without requiring it in the effect deps array. The effect re-registers only
 * when `isOpen` changes, which removes the need for the `eslint-disable`
 * comment that appeared at every call-site.
 */
export function useEscapeHandler(isOpen: boolean, onClose: () => void): void {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [isOpen]);
}
